// REFACTORED
import { useState, useCallback, useEffect } from "react";
import { employeeTransactionService } from "../services/employeeTransactionService";
import { SALARYTYPES } from "../../utils/enums";

export const useEmployeeTransaction = ({ user, userAccess, showGeneralMessage, employeeId = null, apiFilterPayload }) => {
  const [loading, setLoading] = useState(false);
  const [dropdownsLoading, setDropdownsLoading] = useState(false);
  const [requestLoading, setRequestLoading] = useState(false);

  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState([]);

  const [salaryData, setSalaryData] = useState({ daily: [], monthly: [], quotaBased: [] });
  const [options, setOptions] = useState({
    shiftSchedules: [],
    classifications: [],
    employments: [],
    payTypes: [],
    salaryTypes: [],
    employeeTypes: [],
  });

  // 1. Fetch Main Data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // MODIFIED: Pass the filters from apiFilterPayload
      const filters = apiFilterPayload || []; // THis will ensure that if apiFilterPayload is undefined, it defaults to an empty array, preventing potential errors in the service layer.
      const result = await employeeTransactionService.getAll(filters, employeeId);
      setData(result);
      setOriginalData(result);

    } catch (error) {
      showGeneralMessage("Failed to load transactions", false);
    } finally {
      setLoading(false);
    }
  }, [employeeId, showGeneralMessage, apiFilterPayload]);

  // 2. Fetch Dropdowns
  const fetchDropdowns = useCallback(async () => {
    setDropdownsLoading(true);
    try {
      const metadata = await employeeTransactionService.getMetadata();
      setOptions(metadata.options);
      setSalaryData(metadata.salaryData);
    } catch (error) {
      console.error("Dropdown fetch error", error);
    } finally {
      setDropdownsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchDropdowns();
  }, [fetchData, fetchDropdowns]);

  // 3. Search Filter
  const handleFilter = useCallback((value) => {
    if (!value) return setData(originalData);
    const lower = value.toLowerCase();
    setData(originalData.filter(f =>
      f?.employeeNumber?.toLowerCase().includes(lower) ||
      f?.employeeName?.toLowerCase().includes(lower) ||
      f?.vesselName?.toLowerCase().includes(lower)
    ));
  }, [originalData]);

  // 4. Payload Builder (Standardized for Service)
  const buildPayload = (form, isAdd = false) => ({
    recipientEmployeePersonalInformationId: parseInt(form.recipientEmployeePersonalInformationId),
    destinationVesselId: form.destinationVesselId ? parseInt(form.destinationVesselId) : null,
    fbReference: form.fbReference || "",
    effectiveFrom: form.effectiveFrom || "",
    transactionCode: form.transactionCode || "",
    remarks: form.remarks || "",
    isActive: form.isActive ?? true,
    isOnLeave: form?.isOnLeave,
    isWithTime: form?.isWithTime,
    isWithBenefits: form?.isWithBenefits,

    assignedEmployeePersonalInformationId: parseInt(form.assignedEmployeePersonalInformationId),
    assignedToEmployeePersonalInformationId: parseInt(form.assignedToEmployeePersonalInformationId),
    medicalClearanceId: form.medicalClearanceId ? parseInt(form.medicalClearanceId) : null,
    tempEmployeeTypeId: form.tempEmployeeTypeId ? parseInt(form.tempEmployeeTypeId) : null,
    classificationId: form.classificationId ? parseInt(form.classificationId) : null,
    departmentId: form.departmentId ? parseInt(form.departmentId) : null,
    tempPositionId: form.tempPositionId ? parseInt(form.tempPositionId) : null,
    tempVesselId: form.tempVesselId ? parseInt(form.tempVesselId) : null,
    tempEmployeeStatusId: form.tempEmployeeStatusId ? parseInt(form.tempEmployeeStatusId) : null,
    employmentId: form.employmentId ? parseInt(form.employmentId) : null,
    destinationHeadId: form.destinationHeadId ? parseInt(form.destinationHeadId) : null,
    salaryType: form.salaryType || "",
    maintenanceSalaryCombinationV2Id: form.salaryType === SALARYTYPES.MAINTENANCESALARYCOMBINATION ? parseInt(form.maintenanceSalaryCombinationV2Id) : null,
    rateAmount: form.rateAmount ? parseFloat(form.rateAmount) : null,
    entitledToOvertime: form.entitledToOvertime ?? false,

    // Tab 4
    shiftScheduleId: form.shiftScheduleId ? parseInt(form.shiftScheduleId) : null,
    restDay: form.restDay || "",

    // Tab 5
    employeeStatusMaintenanceId: form.employeeStatusMaintenanceId ? parseInt(form.employeeStatusMaintenanceId) : null,

    payTypeId: form.payTypeId ? parseInt(form.payTypeId) : null,
    contractFrom: form.contractFrom || null,
    contractTo: form.contractTo || null,
    status: form.status || "pending",
    ...(isAdd && { createdById: parseInt(user?.id) }),
    updatedById: parseInt(user?.id),
  });

  // 5. Actions
  const handleAdd = async (form, onSuccess) => {
    // console.log('Final bulkData:', JSON.stringify(payload, null, 2));
    setRequestLoading(true);
    try {
      const payload = buildPayload(form, true);
      await employeeTransactionService.create(payload);
      showGeneralMessage("Transaction created successfully!", true);
      await fetchData();
      onSuccess?.();
    } catch (error) {
      showGeneralMessage("Error creating transaction", false);
      console.error("Debug error", error);
      console.error("Debug error", error?.message);
    } finally {
      setRequestLoading(false);
    }
  };

  const handleUpdate = async (form, recordId, onSuccess) => {
    setRequestLoading(true);
    try {
      const payload = buildPayload(form, false);
      await employeeTransactionService.update(recordId, payload);
      showGeneralMessage("Transaction updated successfully!", true);
      await fetchData();
      onSuccess?.();
    } catch (error) {
      showGeneralMessage("Error updating transaction", false);
      console.error("Debug error", error);
      console.error("Debug error", error?.message);
    } finally {
      setRequestLoading(false);
    }
  };

  const handleSubmitForBypassApproval = async (employeeTransactionId) => {
    const userId = user?.id ?? null;
    setRequestLoading(true);
    try {
      const payload = {
        userId: userId
      }
      await employeeTransactionService.submitForBypassApproval(employeeTransactionId, payload);
      showGeneralMessage("Transaction submitted!", true);
      await fetchData();
    } catch (error) {
      showGeneralMessage(error?.response?.data || "Error submitting transaction", false);
      console.error("Debug Error:", error);
      console.error("Debug error", error?.message);
    } finally {
      setRequestLoading(false);
    }
  }

  const handleSubmitForApproval = async (employeeTransactionId) => {
    setRequestLoading(true);
    try {
      await employeeTransactionService.markAsForApproval(employeeTransactionId);
      showGeneralMessage("Transaction submitted for approval!", true);
      await fetchData();
    } catch (error) {
      showGeneralMessage(error?.response?.data || "Error submitting transaction for approval", false);
      console.log("Submit for Approval Error:", error.response);
      console.error("Debug error", error?.message);
    } finally {
      setRequestLoading(false);
    }
  };

  const handleMarkAsDeparted = async (employeeTransactionId) => {
    setRequestLoading(true);
    try {
      await employeeTransactionService.markAsDeparted(employeeTransactionId);
      console.log("Marked as Departed:", employeeTransactionId); // Debug log
      showGeneralMessage("Employee has been marked as departed!", true);
      await fetchData();
    } catch (error) {
      showGeneralMessage("Error marking employee as departed", false);
      console.error("Debug error", error?.message);
    } finally {
      setRequestLoading(false);
    }
  };

  const handleMarkAsArrived = async (employeeTransactionId) => {
    setRequestLoading(true);
    try {
      await employeeTransactionService.markAsArrived(employeeTransactionId);
      showGeneralMessage("Employee has arrived, transaction is complete!", true);
      await fetchData();
    } catch (error) {
      showGeneralMessage("Error marking employee as complete", false);
      console.error("Debug error", error?.message);
    } finally {
      setRequestLoading(false);
    }
  };

  return {
    loading,
    dropdownsLoading,
    requestLoading,
    data,
    options,
    salaryData,
    fetchData,
    handleFilter,
    handleAdd,
    handleUpdate,
    handleMarkAsDeparted,
    handleSubmitForBypassApproval,
    handleSubmitForApproval,
    handleMarkAsArrived,
  };
};